// Profil-Nachbau F-17 (PF-782; BAU-RECHT.md 3, TEIL-3 SP3-11 bis SP3-14): alle Wörter der Nutzungsrichtlinie an einer Stelle
// und die reinen Funktionen dazu, ohne React und ohne Anfrage. Abweichungen aus BAU-RECHT 3.1: Die österreichische Vorlage
// steht als gekennzeichneter Entwurf da, solange sie nicht juristisch geprüft ist (F-17, R-7), und nirgends steht ein
// Firmenlogo (MK-1). „Änderungen verwerfen“ aus SP3-11 heißt „Eingaben verwerfen“: das Wörterverzeichnis (V-27,
// frontend/tests/woerterverzeichnis.test.mjs) führt für das Verwerfen ein Wort, das aus VERWERFEN (administration/components.tsx).
// Geprüft in frontend/tests/richtlinie-pflicht.test.mjs.
import type { ErklaerInhalt } from '../../components/Erklaerung';

/** Der Server sperrt ein Konto bis zur Zustimmung mit 403 und diesem Code (BAU-RECHT 3.2, wie TWO_FACTOR_SETUP_REQUIRED). */
export const RICHTLINIE_PFLICHT = 'USAGE_POLICY_REQUIRED';

export function istRichtlinienFehler(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } } | null)?.response;
  return response?.status === 403 && response.data?.code === RICHTLINIE_PFLICHT;
}

/** F-23 (PF-NB-recht-3, Fehlersuche Nr. 7): Die interne Organisation hat keine Nutzungsrichtlinie; der Server antwortet dann mit
 *  409 und diesem Code (backend/src/administration/richtlinie/service.ts). Die Seite zeigt dafür den ruhigen Satz nurKundenfirmen. */
export const RICHTLINIE_NUR_KUNDENFIRMEN = 'POLICY_NOT_AVAILABLE';
export const istNurKundenfirmen = (code: string | null | undefined) => code === RICHTLINIE_NUR_KUNDENFIRMEN;

/** Code einer Antwort des Servers (409 POLICY_STALE, 429 POLICY_REMINDER_TOO_SOON …), sonst null. */
export function fehlerCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code;
  return typeof code === 'string' ? code : null;
}

// SP3-11 „Sprache hinzufügen“: Deutsch zuerst, dann die Sprachen des Menüs in seiner Reihenfolge (Prüfbedingung der Migration).
export const SPRACHEN = [
  { code: 'de', name: 'Deutsch' }, { code: 'en', name: 'English' }, { code: 'fr', name: 'Français' }, { code: 'it', name: 'Italiano' },
  { code: 'es', name: 'Español' }, { code: 'pl', name: 'Polski' }, { code: 'hr', name: 'Hrvatski' }, { code: 'sl', name: 'Slovenščina' },
  { code: 'hu', name: 'Magyar' }, { code: 'tr', name: 'Türkçe' },
] as const;
export type Sprache = typeof SPRACHEN[number]['code'];
export const sprachName = (code: string) => SPRACHEN.find(sprache => sprache.code === code)?.name ?? code.toUpperCase();
/** Tabelle „Fassungen“, Spalte „Sprachen“: „DE, EN“. */
export const sprachKuerzel = (codes: string[]) => codes.map(code => code.toUpperCase()).join(', ');
/** Deutsch zuerst, dann wie im Menü. */
export const sprachReihenfolge = (codes: string[]) => [...codes].sort((a, b) => rang(a) - rang(b));
const rang = (code: string) => { const stelle = SPRACHEN.findIndex(sprache => sprache.code === code); return stelle < 0 ? SPRACHEN.length : stelle; };

export const TEXT_GRENZE = 50000;
export const NOTIZ_GRENZE = 500;

// Tausender mit Punkt wie in den Wortlauten („50.000 Zeichen“), Datum und Uhrzeit de-AT nach Wiener Zeit.
const zahl = (wert: number) => String(wert).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const WIEN = 'Europe/Vienna';
export const datum = (iso: string) => new Intl.DateTimeFormat('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: WIEN }).format(new Date(iso));
export const uhrzeit = (iso: string) => new Intl.DateTimeFormat('de-AT', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: WIEN }).format(new Date(iso));
export const datumZeit = (iso: string) => `${datum(iso)}, ${uhrzeit(iso)}`;

export const RICHTLINIE_TEXTE = {
  titel: 'Nutzungsrichtlinie',
  // F-24 (SPEC-recht-b 3.2, U2): kurze Unterzeile wie meinGPT; die Folge der Veröffentlichung sagt achtung, den Ablauf das Fenster wie.
  beschreibung: (produkt: string) => `Regeln Ihrer Firma für ${produkt}`,
  // F-24 (SPEC-recht-b 3.2, F2): Fenster im Seitenkopf; Sätze aus der bisherigen Unterzeile, aus keineFassung, zustimmungenLeer und
  // dem Dialog „Fassung {n} veröffentlichen?“.
  wie: {
    frage: 'Wie funktioniert die Bestätigung?',
    teile: [
      { titel: '1. Veröffentlichen', text: 'Jede veröffentlichte Fassung bekommt eine eigene Nummer. Alle Mitglieder bekommen dazu eine E-Mail.' },
      { titel: '2. Bestätigen', text: 'Jedes Mitglied bestätigt jede neue Fassung, bevor es weiterarbeitet. Laufende Recherchen laufen zu Ende.' },
      { titel: '3. Nachweis', text: 'Wer welche Fassung bestätigt hat, sehen Sie unter „Zustimmungen“. Ohne Richtlinie müssen Mitglieder nichts bestätigen.' },
    ],
    hilfe: 'nutzungsrichtlinie',
  } as ErklaerInhalt,
  veroeffentlichen: 'Fassung veröffentlichen',
  laden: 'Nutzungsrichtlinie wird geladen …',
  ladeFehler: 'Die Nutzungsrichtlinie konnte nicht geladen werden.',
  erneut: 'Erneut versuchen',
  // F-23 (PF-NB-recht-3): direkter Aufruf für die interne Organisation (409 POLICY_NOT_AVAILABLE), ruhig statt Fehlerkasten.
  nurKundenfirmen: 'Eine Nutzungsrichtlinie legen nur Kundenfirmen für ihre Mitglieder fest. Für die interne Organisation ist sie nicht nötig.',
  konflikt: 'Inzwischen hat jemand eine neuere Fassung veröffentlicht. Ihr Text bleibt im Feld. Bitte laden Sie neu und prüfen Sie ihn.',
  neuLaden: 'Neu laden',
  // Abschnitt „Stand“
  stand: 'Stand',
  aktuelleFassung: 'Aktuelle Fassung',
  keineFassung: 'Noch keine Richtlinie veröffentlicht.',
  alleFassungen: 'Alle Fassungen',
  zustimmung: 'Zustimmung',
  zustimmungenAnsehen: 'Zustimmungen ansehen',
  // Abschnitt „Text“
  text: 'Text',
  sprache: 'Sprache',
  spracheHinzufuegen: 'Sprache hinzufügen',
  spracheEntfernen: 'Diese Sprache entfernen',
  entfernen: 'Entfernen',
  vorlageEinsetzen: 'Österreichische Vorlage einsetzen',
  // F-24 (SPEC-recht-b 3.2, T2): das Wort des Info-Symbols hinter „Österreichische Vorlage einsetzen“; der Tooltip zeigt vorlageSatz.
  vorlageWort: 'Österreichische Vorlage',
  vorlageSatz: 'Die Vorlage ist ein Vorschlag von CLAiRIS und kein Rechtsrat. Bitte prüfen Sie sie und passen Sie sie an Ihre Firma an.',
  // BAU-RECHT 3.1 Nr. 3 (F-17): solange die Fassung der Vorlage nicht juristisch geprüft ist, als Info neben dem Knopf und in der Rückfrage.
  vorlageEntwurf: 'Entwurf: noch nicht juristisch geprüft',
  vorlageFrage: 'Vorlage einsetzen?',
  vorlageFrageEntwurf: 'Diese Vorlage ist ein Entwurf und noch nicht juristisch geprüft.',
  vorlageFrageText: 'Der Text in diesem Feld wird ersetzt. Gültig wird er erst mit „Fassung veröffentlichen“.',
  vorschau: 'Vorschau',
  schliessen: 'Schließen',
  formatierung: 'Formatierung',
  ueberschrift: 'Überschrift',
  fett: 'Fett',
  aufzaehlung: 'Aufzählung',
  nummerierung: 'Nummerierung',
  link: 'Link',
  feldText: 'Text der Richtlinie',
  platzhalter: 'Noch kein Text. Schreiben Sie selbst oder setzen Sie die österreichische Vorlage ein.',
  feldNotiz: 'Was hat sich geändert?',
  notizHinweis: 'Diesen Satz sehen die Mitglieder im Bestätigungsfenster.',
  achtung: 'Nach dem Veröffentlichen kann niemand in Ihrer Firma weiterarbeiten, bevor er die neue Fassung bestätigt hat. Das gilt auch für Sie.',
  veroeffentlichenKnopf: 'Veröffentlichen',
  abbrechen: 'Abbrechen',
  keineAenderung: 'Keine Änderung zum Veröffentlichen.',
  verwerfen: 'Eingaben verwerfen',
  nurLesen: 'Ändern dürfen Personen mit dem Recht „Nutzungsrichtlinie ändern und veröffentlichen“.',
  // Abschnitt „Fassungen“ (SP3-12)
  fassungen: 'Fassungen',
  spalteFassung: 'Fassung',
  spalteVeroeffentlicht: 'Veröffentlicht',
  spalteVon: 'von',
  spalteSprachen: 'Sprachen',
  spalteBestaetigt: 'Bestätigt',
  spalteGeaendert: 'Was hat sich geändert?',
  ansehen: 'Ansehen',
  uebernehmen: 'Als neue Fassung übernehmen',
  keineFassungen: 'Noch keine Fassung veröffentlicht.',
  entfernt: 'entfernt',
  neu: 'neu',
  // Abschnitt „Zustimmungen“ (SP3-14)
  zustimmungen: 'Zustimmungen',
  filterAlle: 'Alle',
  filterBestaetigt: 'Bestätigt',
  filterOffen: 'Offen',
  filterFassung: 'Fassung',
  suche: 'Name suchen …',
  spalteName: 'Name',
  spalteRolle: 'Rolle',
  spalteStand: 'Stand',
  spalteSprache: 'Sprache',
  erinnerung: 'Erinnerung senden',
  erinnerungText: 'Sie bekommen eine E-Mail mit der Bitte um Bestätigung.',
  export: 'Liste exportieren (CSV)',
  // Export mit Passwort wie die Team-Nutzung (administration/organization/TeamNutzung.tsx).
  exportSatz: 'Bitte bestätigen Sie den Export mit Ihrem Passwort. Der Export wird protokolliert.',
  passwort: 'Ihr Passwort',
  herunterladen: 'Herunterladen',
  zustimmungenLeer: 'Es gibt noch keine Fassung. Sobald Sie eine veröffentlichen, sehen Sie hier, wer sie bestätigt hat.',
  geloescht: 'Gelöschtes Konto',
  ausgetreten: '(nicht mehr in der Firma)',
  nochNicht: 'Noch nicht bestätigt',
  // Filter oder Suche ohne Treffer (wie „Keine Firma gefunden.“ im KI-Cockpit).
  keinePerson: 'Keine Person gefunden.',
  // Zustimmungsseite (SP3-13)
  haken: 'Ich habe die Nutzungsrichtlinie gelesen und halte mich daran.',
  bestaetigen: 'Bestätigen und weiterarbeiten',
  fehltBestaetigung: 'Bestätigung',
  anzeigenAuf: 'Anzeigen auf:',
  wasGeaendert: 'Was hat sich geändert?',
  fragenDazu: 'Fragen dazu?',
  abmelden: 'Abmelden',
  nichtZu: 'Ich stimme nicht zu',
  nichtZuText: 'Ohne Zustimmung können Sie CLAiRIS in dieser Firma nicht nutzen. Sie können sich abmelden oder die Firma verlassen.',
  bestaetigenFehler: 'Die Bestätigung hat nicht geklappt. Bitte versuchen Sie es erneut.',
  neuereFassung: 'Inzwischen gibt es eine neuere Fassung. Wir zeigen sie Ihnen jetzt.',
  // „Mein Zugang“ (BAU-RECHT 3.1 Nr. 7)
  lesen: 'Lesen',
} as const;

/** Abschnitt „Stand“, Zeile „Aktuelle Fassung“. Ohne Namen (gelöschtes Konto) „Gelöschtes Konto“. */
export const standAktuell = (nummer: number, am: string, von: string | null | undefined) =>
  `Fassung ${nummer} vom ${datum(am)}, veröffentlicht von ${von || RICHTLINIE_TEXTE.geloescht}`;

/** Der Quote-Satz (SP3-11 „Zustimmung“, SP3-14 Kennzahl). prozent vom Server, sonst gerundet aus den Zahlen. */
export function quoteSatz(quote: { bestaetigt: number; gesamt: number; prozent?: number | null }, nummer: number): string {
  const prozent = typeof quote.prozent === 'number' ? quote.prozent : quote.gesamt ? Math.round((quote.bestaetigt * 100) / quote.gesamt) : 0;
  return `${quote.bestaetigt} von ${quote.gesamt} Mitgliedern haben Fassung ${nummer} bestätigt (${prozent} %).`;
}
export const pausiertSatz = (anzahl: number) => anzahl === 1
  ? '1 pausiertes Mitglied ist nicht mitgezählt.' : `${anzahl} pausierte Mitglieder sind nicht mitgezählt.`;

export const zaehler = (zeichen: number) => `${zahl(zeichen)} von ${zahl(TEXT_GRENZE)} Zeichen`;
export const veroeffentlichenFrage = (nummer: number) => `Fassung ${nummer} veröffentlichen?`;
export const veroeffentlichenText = (mitglieder: number) =>
  `${mitglieder} Mitglieder müssen sie bei ihrem nächsten Klick in CLAiRIS bestätigen. Laufende Recherchen laufen zu Ende. Alle bekommen eine E-Mail.`;
export const veroeffentlichtSatz = (nummer: number) => `Fassung ${nummer} ist veröffentlicht. Bitte bestätigen Sie sie jetzt selbst.`;
export const spracheEntfernenFrage = (sprache: string) =>
  `${sprachName(sprache)} entfernen? Mitglieder mit dieser Sprache sehen nach dem Veröffentlichen die deutsche Fassung.`;
/** Es fehlt noch: der Text einer hinzugefügten Sprache (Deutsch ist Pflicht). */
export const fehltText = (sprache: string) => `${RICHTLINIE_TEXTE.feldText} (${sprachName(sprache)})`;
export const vergleichenKnopf = (nummer: number) => `Mit Fassung ${nummer - 1} vergleichen`;
export const bestaetigtVon = (bestaetigt: number, gesamt: number) => `${bestaetigt} von ${gesamt}`;
export const weitereAktionenFassung = (nummer: number) => `Weitere Aktionen für Fassung ${nummer}`;

export type ZustimmungStand = 'BESTAETIGT' | 'AELTER' | 'OFFEN';
/** Spalte „Stand“ der Zustimmungen (SP3-14). */
export function standText(eintrag: { stand: ZustimmungStand; fassung: number | null; am: string | null }): string {
  if (eintrag.stand === 'BESTAETIGT' && eintrag.fassung !== null && eintrag.am) return `Fassung ${eintrag.fassung} bestätigt am ${datumZeit(eintrag.am)}`;
  if (eintrag.stand === 'AELTER' && eintrag.fassung !== null) return `Ältere Fassung ${eintrag.fassung} bestätigt`;
  return RICHTLINIE_TEXTE.nochNicht;
}
export const erinnerungFrage = (anzahl: number) => `Erinnerung an ${anzahl} Mitglieder senden?`;
/** G-18: die Meldung am Knopf nach dem Senden. */
export const erinnerungGesendet = (anzahl: number) => `Erinnerung an ${anzahl} Mitglieder gesendet.`;
export const erinnerungAb = (ab: string) => `Die nächste Erinnerung ist ab ${datumZeit(ab)} möglich.`;
/** Ohne Angabe des Servers: 24 Stunden nach dem Senden (SP3-14). */
export const erinnerungAbJetzt = (jetzt: Date) => new Date(jetzt.getTime() + 24 * 60 * 60 * 1000).toISOString();

/** Kopf der Zustimmungsseite, der Vorschau und des Lesefensters; nie ein Firmenlogo (MK-1). */
export const zustimmungTitel = (firma: string) => firma ? `Nutzungsrichtlinie von ${firma}` : RICHTLINIE_TEXTE.titel;
export const fassungVom = (nummer: number, am: string) => `Fassung ${nummer} vom ${datum(am)}`;
export const zustimmungZeile = (nummer: number, am: string) => `${fassungVom(nummer, am)}. Bitte lesen und bestätigen Sie sie, um weiterzuarbeiten.`;
export const unterschiedeKnopf = (nummer: number) => `Unterschiede zu Fassung ${nummer} anzeigen`;
/** „Organisation verlassen“ (SP3-13) mit dem Begriff der Marke wie der Austritt unter „Meine Daten“ (pages/einstellungen/Unterseiten.tsx). */
export const verlassenKnopf = (begriff: string) => `${begriff} verlassen`;
/** Der Haken der Zustimmungsseite und der Einladung, mit „(Pflicht)“ wie jedes Pflichtfeld (GB-02). */
export const hakenText = `${RICHTLINIE_TEXTE.haken} (Pflicht)`;
export const danke = (nummer: number) => `Danke. Fassung ${nummer} ist bestätigt.`;
/** „Mein Zugang“ und das Lesefenster. */
export const bestaetigtSatz = (nummer: number, am: string) => `Sie haben Fassung ${nummer} am ${datum(am)} bestätigt.`;

// ---- Entwurf im Feld: geändert gegenüber der veröffentlichten Fassung, was fehlt, Formatierungsleiste ----

/** Wie der Server vergleicht: Zeilenende „\n“, NFC, ohne Leerraum am Ende (TEIL-3 SP3-11 „Eingabe“). */
export const normalText = (text: string) => text.replace(/\r\n?/g, '\n').normalize('NFC').replace(/\s+$/, '');

export type Entwuerfe = Record<string, string>;
export const entwuerfeAus = (texte: { sprache: string; text: string }[] | null | undefined): Entwuerfe =>
  Object.fromEntries((texte?.length ? texte : [{ sprache: 'de', text: '' }]).map(t => [t.sprache, t.text]));

/** Gibt es etwas zu veröffentlichen? Eine entfernte oder hinzugefügte Sprache zählt, ein geänderter Text auch. */
export function istGeaendert(entwuerfe: Entwuerfe, veroeffentlicht: { sprache: string; text: string }[] | null | undefined): boolean {
  const alt = new Map((veroeffentlicht ?? []).map(t => [t.sprache, normalText(t.text)]));
  const neu = Object.entries(entwuerfe).filter(([, text]) => normalText(text) !== '');
  if (!alt.size) return neu.length > 0;
  if (Object.keys(entwuerfe).length !== alt.size) return true;
  return Object.entries(entwuerfe).some(([sprache, text]) => alt.get(sprache) !== normalText(text));
}

/** „Es fehlt noch: …“ vor dem Veröffentlichen: Deutsch und jede hinzugefügte Sprache brauchen Text. */
export const fehlendeTexte = (entwuerfe: Entwuerfe) =>
  sprachReihenfolge(Object.keys(entwuerfe).includes('de') ? Object.keys(entwuerfe) : ['de', ...Object.keys(entwuerfe)])
    .filter(sprache => !normalText(entwuerfe[sprache] ?? '')).map(fehltText);

export type Formatierung = 'ueberschrift' | 'fett' | 'aufzaehlung' | 'nummerierung' | 'link';
export type Auswahl = { text: string; start: number; ende: number };

/** Formatierungsleiste (SP3-11): setzt Markdown um die Auswahl und gibt die neue Auswahl zurück. */
export function formatieren({ text, start, ende }: Auswahl, art: Formatierung): Auswahl {
  const auswahl = text.slice(start, ende);
  if (art === 'fett') {
    const neu = `**${auswahl}**`;
    return { text: text.slice(0, start) + neu + text.slice(ende), start: start + 2, ende: start + 2 + auswahl.length };
  }
  if (art === 'link') {
    const neu = `[${auswahl}](https://)`;
    // Mit Auswahl steht die Schreibmarke danach in der Adresse, ohne Auswahl zwischen den eckigen Klammern.
    const marke = auswahl ? start + auswahl.length + 3 + 'https://'.length : start + 1;
    return { text: text.slice(0, start) + neu + text.slice(ende), start: marke, ende: marke };
  }
  // Zeilenweise: von der ersten bis zur letzten berührten Zeile.
  const zeilenAnfang = text.lastIndexOf('\n', start - 1) + 1;
  const zeilenEndeRoh = text.indexOf('\n', ende > start && text[ende - 1] === '\n' ? ende - 1 : ende);
  const zeilenEnde = zeilenEndeRoh < 0 ? text.length : zeilenEndeRoh;
  const block = text.slice(zeilenAnfang, zeilenEnde).split('\n');
  const neu = block.map((zeile, index) => {
    if (art === 'ueberschrift') return /^#{1,3}\s/.test(zeile) ? zeile : `## ${zeile}`;
    if (art === 'aufzaehlung') return /^-\s/.test(zeile) ? zeile : `- ${zeile}`;
    return /^\d+\.\s/.test(zeile) ? zeile : `${index + 1}. ${zeile}`;
  }).join('\n');
  return { text: text.slice(0, zeilenAnfang) + neu + text.slice(zeilenEnde), start: zeilenAnfang, ende: zeilenAnfang + neu.length };
}
